'use client';

import React from 'react';
import styled from 'styled-components';
import { Breadcrumbs } from '@/components/ui-components';
import DataTable from '@/components/tables/DataTable';
import { DataTableColumn } from '@/types/components';
import { LogAnalyzerAlertData, LogAnalyzerDomain, LogAnalyzerTopError, LogAnalyzerThresholdComparison } from '@/types/alert';
import { i18n } from '@/config/i18n';
import { theme } from '@/config/theme';
import { formatDate } from '@/utils/dateUtils';

const SectionCard = styled.div`
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 8px;
  margin: 24px;
  position: relative;
  overflow: hidden;
`;

const SectionContent = styled.div`
  padding: 0 24px 24px;
`;

const MetricCard = styled.div`
  background: ${theme.colors.gray[50]};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${theme.colors.border.primary};
  
  &:last-child {
    border-bottom: none;
  }
`;

const MetricLabel = styled.span`
  font-size: 13px;
  color: ${theme.colors.text.secondary};
  font-weight: 500;
`;

const MetricValue = styled.span<{ $color?: string }>`
  font-size: 14px;
  color: ${({ $color }) => $color || theme.colors.text.primary};
  font-weight: 600;
`;

const ThresholdCard = styled.div`
  background: ${theme.colors.gray[50]};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const ThresholdRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ThresholdItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ThresholdLabel = styled.span`
  font-size: 12px;
  color: ${theme.colors.text.secondary};
  font-weight: 500;
`;

const ThresholdValue = styled.span<{ $isExceeded?: boolean }>`
  font-size: 14px;
  color: ${({ $isExceeded }) => $isExceeded ? '#ef4444' : theme.colors.text.primary};
  font-weight: 600;
`;

interface LogAnalyzerDataSectionProps {
  data: LogAnalyzerAlertData;
}

export const LogAnalyzerDataSection: React.FC<LogAnalyzerDataSectionProps> = ({ data }) => {
  const { metrics, alert_type, threshold_comparison } = data;

  // Get alert type label
  const getAlertTypeLabel = () => {
    switch (alert_type) {
      case 'error_spike':
        return i18n.triage.detail.logAnalyzer.alertType.errorSpike;
      case 'high_volume':
        return i18n.triage.detail.logAnalyzer.alertType.highVolume;
      case 'multiple_domains':
        return i18n.triage.detail.logAnalyzer.alertType.multipleDomains;
      default:
        return 'Log Analyzer Alert';
    }
  };

  // Domain columns for DataTable
  const domainColumns: DataTableColumn<LogAnalyzerDomain>[] = [
    {
      key: 'domain_name',
      header: i18n.triage.detail.logAnalyzer.domain.domainName,
      width: '200px',
      bold: true,
    },
    {
      key: 'domain_url',
      header: i18n.triage.detail.logAnalyzer.domain.domainUrl,
      width: '300px',
      render: (value: string) => (
        <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: theme.colors.accent, textDecoration: 'none' }}>
          {value}
        </a>
      ),
    },
    {
      key: 'error_count',
      header: i18n.triage.detail.logAnalyzer.domain.errorCount,
      width: '120px',
      align: 'center' as const,
    },
    {
      key: 'first_error',
      header: i18n.triage.detail.logAnalyzer.domain.firstError,
      width: '180px',
      render: (value: string) => formatDate(value, true),
    },
    {
      key: 'last_error',
      header: i18n.triage.detail.logAnalyzer.domain.lastError,
      width: '180px',
      render: (value: string) => formatDate(value, true),
    },
  ];

  // Top errors columns for DataTable
  const topErrorsColumns: DataTableColumn<LogAnalyzerTopError>[] = [
    {
      key: 'error_message',
      header: i18n.triage.detail.logAnalyzer.error.errorMessage,
      width: '400px',
    },
    {
      key: 'count',
      header: i18n.triage.detail.logAnalyzer.error.count,
      width: '100px',
      align: 'center' as const,
    },
    {
      key: 'percentage',
      header: i18n.triage.detail.logAnalyzer.error.percentage,
      width: '120px',
      align: 'center' as const,
      render: (value: number) => `${value.toFixed(2)}%`,
    },
    {
      key: 'first_occurrence',
      header: i18n.triage.detail.logAnalyzer.error.firstOccurrence,
      width: '180px',
      render: (value: string) => formatDate(value, true),
    },
    {
      key: 'latest_occurrence',
      header: i18n.triage.detail.logAnalyzer.error.latestOccurrence,
      width: '180px',
      render: (value: string) => formatDate(value, true),
    },
  ];

  // Render threshold comparison
  const renderThresholdComparison = (threshold: LogAnalyzerThresholdComparison) => (

    <>
    <Breadcrumbs items={[{ label: i18n.triage.detail.sections.thresholdComparison }]}  />
    <ThresholdCard>
      <ThresholdRow>
        <ThresholdItem>
          <ThresholdLabel>{i18n.triage.detail.logAnalyzer.threshold.thresholdName}</ThresholdLabel>
          <ThresholdValue>{threshold.threshold_name}</ThresholdValue>
        </ThresholdItem>
        <ThresholdItem>
          <ThresholdLabel>{i18n.triage.detail.logAnalyzer.threshold.thresholdExceeded}</ThresholdLabel>
          <ThresholdValue $isExceeded={threshold.threshold_exceeded}>
            {threshold.threshold_exceeded ? 'Yes' : 'No'}
          </ThresholdValue>
        </ThresholdItem>
      </ThresholdRow>
      <ThresholdRow>
        <ThresholdItem>
          <ThresholdLabel>{i18n.triage.detail.logAnalyzer.threshold.thresholdValue}</ThresholdLabel>
          <ThresholdValue>{threshold.threshold_value}</ThresholdValue>
        </ThresholdItem>
        <ThresholdItem>
          <ThresholdLabel>{i18n.triage.detail.logAnalyzer.threshold.actualValue}</ThresholdLabel>
          <ThresholdValue $isExceeded={threshold.threshold_exceeded}>
            {threshold.actual_value}
          </ThresholdValue>
        </ThresholdItem>
      </ThresholdRow>
      <ThresholdRow>
        <ThresholdItem>
          <ThresholdLabel>{i18n.triage.detail.logAnalyzer.threshold.exceededBy}</ThresholdLabel>
          <ThresholdValue $isExceeded={threshold.threshold_exceeded}>
            {threshold.exceeded_by_value}
          </ThresholdValue>
        </ThresholdItem>
        <ThresholdItem>
          <ThresholdLabel>{i18n.triage.detail.logAnalyzer.threshold.exceededByPercentage}</ThresholdLabel>
          <ThresholdValue $isExceeded={threshold.threshold_exceeded}>
            {threshold.exceeded_by_percentage.toFixed(2)}%
          </ThresholdValue>
        </ThresholdItem>
      </ThresholdRow>
      <ThresholdRow>
        <ThresholdItem>
          <ThresholdLabel>{i18n.triage.detail.logAnalyzer.threshold.percentageOfThreshold}</ThresholdLabel>
          <ThresholdValue $isExceeded={threshold.threshold_exceeded}>
            {threshold.percentage_of_threshold.toFixed(2)}%
          </ThresholdValue>
        </ThresholdItem>
      </ThresholdRow>
    </ThresholdCard>
    </>
  );

  return (
    <>
      {/* Alert Type and Metrics Summary */}
      <SectionCard>
        <Breadcrumbs items={[{ label: i18n.triage.detail.sections.logAnalyzer }]}  />
        <SectionContent>
          <MetricCard>
            <MetricRow>
              <MetricLabel>Alert Type</MetricLabel>
              <MetricValue>{getAlertTypeLabel()}</MetricValue>
            </MetricRow>
            {metrics.current_count !== undefined && (
              <MetricRow>
                <MetricLabel>{i18n.triage.detail.logAnalyzer.metrics.currentCount}</MetricLabel>
                <MetricValue>{metrics.current_count}</MetricValue>
              </MetricRow>
            )}
            {metrics.previous_count !== undefined && (
              <MetricRow>
                <MetricLabel>{i18n.triage.detail.logAnalyzer.metrics.previousCount}</MetricLabel>
                <MetricValue>{metrics.previous_count}</MetricValue>
              </MetricRow>
            )}
            {metrics.increase_percentage !== undefined && (
              <MetricRow>
                <MetricLabel>{i18n.triage.detail.logAnalyzer.metrics.increasePercentage}</MetricLabel>
                <MetricValue $color="#ef4444">{metrics.increase_percentage.toFixed(2)}%</MetricValue>
              </MetricRow>
            )}
            {metrics.total_errors !== undefined && (
              <MetricRow>
                <MetricLabel>{i18n.triage.detail.logAnalyzer.metrics.totalErrors}</MetricLabel>
                <MetricValue>{metrics.total_errors}</MetricValue>
              </MetricRow>
            )}
            {metrics.total_errors_across_domains !== undefined && (
              <MetricRow>
                <MetricLabel>{i18n.triage.detail.logAnalyzer.metrics.totalErrors}</MetricLabel>
                <MetricValue>{metrics.total_errors_across_domains}</MetricValue>
              </MetricRow>
            )}
            {metrics.affected_count !== undefined && (
              <MetricRow>
                <MetricLabel>{i18n.triage.detail.logAnalyzer.metrics.affectedDomains}</MetricLabel>
                <MetricValue>{metrics.affected_count}</MetricValue>
              </MetricRow>
            )}
            {metrics.total_domains !== undefined && (
              <MetricRow>
                <MetricLabel>{i18n.triage.detail.logAnalyzer.metrics.totalDomains}</MetricLabel>
                <MetricValue>{metrics.total_domains}</MetricValue>
              </MetricRow>
            )}
            {metrics.window && (
              <MetricRow>
                <MetricLabel>Time Window</MetricLabel>
                <MetricValue>{metrics.window}</MetricValue>
              </MetricRow>
            )}
          </MetricCard>

          {/* Threshold Comparison */}
          {threshold_comparison && renderThresholdComparison(threshold_comparison)}
          {metrics.threshold_comparison && renderThresholdComparison(metrics.threshold_comparison)}
        </SectionContent>
      </SectionCard>

      {/* Affected Domains */}
      {metrics.domains && metrics.domains?.length > 0 && (
        <>
        <Breadcrumbs items={[{ label: i18n.triage.detail.sections.domains }]}  />
        <SectionCard>
          <SectionContent>
            <DataTable
              style={{ border: 'none' }}
              data={metrics.domains}
              columns={domainColumns}
              pagination={false}
              searchable={false}
              striped={true}
              hoverable={true}
            />
          </SectionContent>
        </SectionCard>
        </>   
      )}

      {/* Top Errors per Domain */}
      {metrics.domains && metrics.domains?.length > 0 && metrics.domains.some(d => d.top_errors && d.top_errors?.length > 0) && (
        <>
          {metrics.domains.map((domain: LogAnalyzerDomain, index: number) => {
            if (!domain.top_errors || domain.top_errors?.length === 0) return null;
            
            return (
                <>
                <Breadcrumbs items={[
                    { label: i18n.triage.detail.sections.topErrors },
                    { label: domain.domain_name }
                  ]}  />
              <SectionCard key={`domain-${index}`}>
            
                <SectionContent>
                  <DataTable
                    style={{ border: 'none' }}
                    data={domain.top_errors}
                    columns={topErrorsColumns}
                    pagination={false}
                    searchable={false}
                    striped={true}
                    hoverable={true}
                  />
                </SectionContent>
              </SectionCard>
              </>
            );
          })}
        </>
      )}
    </>
  );
};

